import { computed } from "@angular/core";
import { ViewState } from "../../../../harmony/core";
import { PaymentType } from "../../../shared/enums/campaign.enums";
import { PostPlatform } from "../../../shared/enums/campaignPost.enums";
import { Partner } from "../../../shared/models/partner";
import { PartnerCampaign } from "../../../shared/models/partnerCampaign";
import { PartnerCheckThirdPartyConnectionsContext } from "./partner-check-third-party-connections-dialog.component";

export enum CheckId {
  GoogleConnection = "googleConnection",
  FbConnection = "fbConnection",
  IgConnection = "igConnection",
  LinkedInConnection = "linkedInConnection",
  FbAdsPermissions = "fbAdsPermissions",
  ShortUrlExists = "shortUrlExists",
  HasVat = "hasVat",
}

type CheckViewState = CheckErrorViewState | CheckSuccessViewState;

class CheckErrorViewState implements ViewState {
  public readonly $type = "error";
  public readonly statusIconPath = "/assets/images/icons/ic_check_fail.svg";
  public readonly instructionsKey: string = "";
  public readonly linkType: "button" | "link" = "link";
  public readonly linkKey: string = "";

  constructor(
    public readonly id: CheckId,
    public readonly isRequired: boolean,
    public readonly ignored: boolean,
  ) {
    switch (id) {
      case CheckId.FbConnection:
      case CheckId.IgConnection:
      case CheckId.GoogleConnection:
      case CheckId.LinkedInConnection:
        this.instructionsKey = "partner.snConnectionsDialog.calendarNeed";
        this.linkKey = `partner.snConnectionsDialog.connectTo.${this.id}`;
        break;
      case CheckId.FbAdsPermissions:
        this.instructionsKey = "partner.snConnectionsDialog.authorizeInfo";
        this.linkKey = "partner.snConnectionsDialog.refreshAndVerify";
        this.linkType = "button";
        break;
      case CheckId.ShortUrlExists:
        this.instructionsKey =
          "partner.campaign.contentCalendar.setupDialog.shortUrlInvalid";
        this.linkKey =
          "partner.campaign.contentCalendar.setupDialog.openShortUrlDialog";
        break;
      case CheckId.HasVat:
        this.instructionsKey = "partner.snConnectionsDialog.vatInfo";
        this.linkKey = "partner.snConnectionsDialog.vatInfoLink";
        break;
    }
  }
}

class CheckSuccessViewState implements ViewState {
  public readonly $type = "success";
  public readonly statusIconPath = "/assets/images/icons/ic_check_pass.svg";

  constructor(
    public readonly id: CheckId,
    public readonly isRequired: boolean,
  ) {}
}

export class SnConnectionsViewState {
  public readonly checks: CheckViewState[];
  public readonly submitText: string;
  public readonly description: string;
  private readonly campaignRequiredConnections = new Set<CheckId>();

  constructor(
    context: PartnerCheckThirdPartyConnectionsContext,
    campaign: PartnerCampaign,
    partner: Partner,
    private readonly thirdPartyConnections?: CheckId[],
  ) {
    if (context === "pre-approve") {
      this.submitText = "shared.ok";
      this.description = "partner.snConnectionsDialog.preApproveDescription";
    } else if (context === "calendar") {
      this.submitText =
        "partner.campaign.contentCalendar.setupDialog.scheduleAllPosts";
      this.description = "partner.snConnectionsDialog.calendarDescription";
    } else if (context === "solve-issues") {
      this.submitText = "partner.snConnectionsDialog.schedule";
      this.description = "partner.snConnectionsDialog.solveIssuesDescription";
    } else {
      this.submitText = "shared.ok";
      this.description = "";
    }

    campaign.getPlatformsInUse().forEach((platform) => {
      switch (platform) {
        case PostPlatform.Facebook:
          this.campaignRequiredConnections.add(CheckId.FbConnection);
          this.campaignRequiredConnections.add(CheckId.IgConnection);
          break;
        case PostPlatform.Google:
          this.campaignRequiredConnections.add(CheckId.GoogleConnection);
          break;
        case PostPlatform.LinkedIn:
          this.campaignRequiredConnections.add(CheckId.LinkedInConnection);
          break;
      }
    });

    this.checks = [
      ...(this.isCheckVisible(CheckId.GoogleConnection)
        ? [
            this.createCheckViewState(
              CheckId.GoogleConnection,
              partner.areGoogleAdsAuthorized,
            ),
          ]
        : []),
      ...(this.isCheckVisible(CheckId.FbConnection)
        ? [
            this.createCheckViewState(
              CheckId.FbConnection,
              partner.isConnectedToFB,
              false,
              partner.doNotHaveFacebookPage,
            ),
          ]
        : []),
      ...(this.isCheckVisible(CheckId.IgConnection)
        ? [
            this.createCheckViewState(
              CheckId.IgConnection,
              partner.isConnectedToInstagram,
              false,
              partner.doNotHaveFacebookPage,
            ),
          ]
        : []),
      ...(this.isCheckVisible(CheckId.LinkedInConnection)
        ? [
            this.createCheckViewState(
              CheckId.LinkedInConnection,
              partner.isConnectedToLinkedIn,
              false,
              partner.doNotHaveLinkedinOrganization,
            ),
          ]
        : []),
    ];

    if (campaign.hasLandingPageRetailerOwnLink) {
      this.checks.push(
        this.createCheckViewState(
          CheckId.ShortUrlExists,
          !campaign.partner.isShortUrlMissing,
          true,
        ),
      );
    }

    if (
      (campaign.paymentType === PaymentType.CoFunded ||
        campaign.paymentType === PaymentType.Self) &&
      campaign.budget > 0
    ) {
      this.checks.push(
        this.createCheckViewState(CheckId.HasVat, !!partner.vatNumber, true),
      );
    }

    if (partner.isConnectedToFB) {
      this.checks.push(
        this.createCheckViewState(
          CheckId.FbAdsPermissions,
          partner.isAuthorizedFBAds,
        ),
      );
    }
  }

  private isCheckVisible(id: CheckId): boolean {
    return Boolean(
      (!this.thirdPartyConnections &&
        this.campaignRequiredConnections.has(id)) ||
        this.thirdPartyConnections?.includes(id),
    );
  }

  protected createCheckViewState(
    id: CheckId,
    connected: boolean,
    isRequired: boolean = false,
    ignored: boolean = false,
  ): CheckViewState {
    return connected
      ? new CheckSuccessViewState(id, isRequired)
      : new CheckErrorViewState(id, isRequired, ignored);
  }

  public readonly canPreApprove = computed(() => {
    const hasMissingRequiredFields = this.checks.find(
      (check) => check instanceof CheckErrorViewState && check.isRequired,
    );

    const hasSomeOptionalFields = this.checks.some(
      (check) => check instanceof CheckSuccessViewState && !check.isRequired,
    );

    return !hasMissingRequiredFields && hasSomeOptionalFields;
  });
}
